import React, { useEffect, useState } from 'react';
import { styles as st } from './Layout';

const VACIO = {
  nombre_cliente: '', telefono_celular: '', email: '', asesor_inmobiliario_id: '', broker_id: '',
  tipo_credito_id: '', tipo_inmueble_id: '', fecha_asignacion: '', valor_inmueble: '', valor_credito_solicitado: '',
  status_cliente_id: '', fecha_ultimo_contacto: '', observaciones: '',
};

// Ancho de referencia de un campo "normal" en el formulario; los porcentajes pedidos
// (50%, 55%, etc.) se calculan a partir de este valor para que los campos queden
// contiguos entre sí (en vez de ocupar una columna fija de grid con espacio muerto).
const FULL = 320;
const pct = (p) => Math.round(FULL * p);

const fila = { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 12 };

function soloDigitos(v) {
  return (v || '').replace(/\D/g, '');
}

function formatTelefono(raw) {
  const d = soloDigitos(raw).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)})${d.slice(2)}`;
  return `(${d.slice(0, 2)})${d.slice(2, 6)}-${d.slice(6)}`;
}

function formatMoney(v) {
  if (v === null || v === undefined || v === '') return '';
  const num = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]/g, '')) : Number(v);
  if (Number.isNaN(num)) return '';
  return '$' + Math.round(num).toLocaleString('en-US');
}

function ordenarAlfabetico(lista) {
  return [...lista].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
}

function ClienteForm({ cliente, catalogos, usuarioActual, onSave, onCancel }) {
  const esBrokerCreando = !cliente && usuarioActual?.tipo_usuario === 'Broker';
  // Un broker editando un cliente ya asignado solo puede tocar los campos de seguimiento
  // (email, tipo de crédito, tipo de inmueble, status, valores y observaciones); el resto queda de solo lectura.
  const esBrokerEditando = !!cliente && usuarioActual?.tipo_usuario === 'Broker';
  const soloLecturaBroker = esBrokerEditando ? { disabled: true, style: { backgroundColor: '#f3f4f6', color: '#374151' } } : {};
  const hoy = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    ...VACIO,
    ...cliente,
    asesor_inmobiliario_id: cliente?.asesor_inmobiliario_id || '',
    broker_id: cliente?.broker_id || (esBrokerCreando ? usuarioActual.broker_id : ''),
    tipo_credito_id: cliente?.tipo_credito_id || '',
    tipo_inmueble_id: cliente?.tipo_inmueble_id || '',
    status_cliente_id: cliente?.status_cliente_id || '',
    // Solo se propone la fecha de hoy en la captura inicial; al editar se respeta la fecha ya guardada.
    fecha_asignacion: cliente?.fecha_asignacion ? cliente.fecha_asignacion.slice(0, 10) : hoy,
    fecha_ultimo_contacto: cliente?.fecha_ultimo_contacto ? cliente.fecha_ultimo_contacto.slice(0, 10) : hoy,
    telefono_celular: formatTelefono(cliente?.telefono_celular),
    valor_inmueble: soloDigitos(cliente?.valor_inmueble != null ? String(cliente.valor_inmueble).split('.')[0] : ''),
    valor_credito_solicitado: soloDigitos(cliente?.valor_credito_solicitado != null ? String(cliente.valor_credito_solicitado).split('.')[0] : ''),
  }));

  const asesoresOrdenados = ordenarAlfabetico(catalogos.asesores);
  const asesorSeleccionado = catalogos.asesores.find((a) => String(a.id) === String(form.asesor_inmobiliario_id));
  const inmobiliariaMostrada = asesorSeleccionado?.inmobiliaria_nombre || cliente?.inmobiliaria_nombre || '';

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      valor_inmueble: form.valor_inmueble === '' ? '' : soloDigitos(form.valor_inmueble),
      valor_credito_solicitado: form.valor_credito_solicitado === '' ? '' : soloDigitos(form.valor_credito_solicitado),
    });
  };

  return (
    <form onSubmit={submit} style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 16 }}>
      {/* Renglón 1: Nombre, Teléfono (50%), Email */}
      <div style={fila}>
        <div>
          <label style={st.label}>Nombre del cliente</label>
          <input
            style={{ ...st.input, width: FULL, ...soloLecturaBroker.style }}
            value={form.nombre_cliente}
            onChange={(e) => setForm({ ...form, nombre_cliente: e.target.value })}
            required
            disabled={soloLecturaBroker.disabled}
          />
        </div>
        <div>
          <label style={st.label}>Teléfono celular</label>
          <input
            style={{ ...st.input, width: pct(0.5), ...soloLecturaBroker.style }}
            value={form.telefono_celular}
            placeholder="(00)0000-0000"
            onChange={(e) => setForm({ ...form, telefono_celular: formatTelefono(e.target.value) })}
            disabled={soloLecturaBroker.disabled}
          />
        </div>
        <div>
          <label style={st.label}>Email</label>
          <input style={{ ...st.input, width: FULL }} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>

      {/* Renglón 2: Asesor inmobiliario, Inmobiliaria, Broker asignado */}
      <div style={fila}>
        <div>
          <label style={st.label}>Asesor inmobiliario</label>
          <select
            style={{ ...st.input, width: pct(0.7), ...soloLecturaBroker.style }}
            value={form.asesor_inmobiliario_id}
            onChange={(e) => setForm({ ...form, asesor_inmobiliario_id: e.target.value })}
            required={!esBrokerCreando}
            disabled={soloLecturaBroker.disabled}
          >
            <option value="">Selecciona…</option>
            {asesoresOrdenados.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Inmobiliaria</label>
          <input style={{ ...st.input, width: pct(0.7), backgroundColor: '#f3f4f6', color: '#374151' }} value={inmobiliariaMostrada} disabled readOnly />
        </div>
        <div>
          <label style={st.label}>Broker asignado</label>
          {esBrokerCreando ? (
            <input style={{ ...st.input, width: pct(0.5), backgroundColor: '#f3f4f6', color: '#374151' }} value={usuarioActual.broker_nombre || ''} disabled readOnly />
          ) : (
            <select
              style={{ ...st.input, width: pct(0.5), ...soloLecturaBroker.style }}
              value={form.broker_id}
              onChange={(e) => setForm({ ...form, broker_id: e.target.value })}
              required
              disabled={soloLecturaBroker.disabled}
            >
              <option value="">Selecciona…</option>
              {catalogos.brokers.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Renglón 3: Fecha de asignación, Tipo de crédito (50%), Tipo de inmueble (50%) */}
      <div style={fila}>
        <div>
          <label style={st.label}>Fecha de asignación</label>
          <input
            style={{ ...st.input, width: pct(0.5), ...soloLecturaBroker.style }}
            type="date"
            value={form.fecha_asignacion}
            onChange={(e) => setForm({ ...form, fecha_asignacion: e.target.value })}
            disabled={soloLecturaBroker.disabled}
          />
        </div>
        <div>
          <label style={st.label}>Tipo de crédito</label>
          <select style={{ ...st.input, width: pct(0.5) }} value={form.tipo_credito_id} onChange={(e) => setForm({ ...form, tipo_credito_id: e.target.value })}>
            <option value="">Selecciona…</option>
            {catalogos.tipoCredito.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Tipo de inmueble</label>
          <select style={{ ...st.input, width: pct(0.5) }} value={form.tipo_inmueble_id} onChange={(e) => setForm({ ...form, tipo_inmueble_id: e.target.value })}>
            <option value="">Selecciona…</option>
            {catalogos.tipoInmueble.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
      </div>

      {/* Renglón 4: Status, Valor de inmueble (55%, $), Valor del crédito solicitado (55%, $), Fecha de último contacto */}
      <div style={fila}>
        <div>
          <label style={st.label}>Status</label>
          <select style={{ ...st.input, width: FULL }} value={form.status_cliente_id} onChange={(e) => setForm({ ...form, status_cliente_id: e.target.value })}>
            <option value="">Selecciona…</option>
            {catalogos.status.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Valor del inmueble</label>
          <input
            style={{ ...st.input, width: pct(0.55) }}
            inputMode="numeric"
            value={formatMoney(form.valor_inmueble)}
            onChange={(e) => setForm({ ...form, valor_inmueble: soloDigitos(e.target.value) })}
          />
        </div>
        <div>
          <label style={st.label}>Valor del crédito solicitado</label>
          <input
            style={{ ...st.input, width: pct(0.55) }}
            inputMode="numeric"
            value={formatMoney(form.valor_credito_solicitado)}
            onChange={(e) => setForm({ ...form, valor_credito_solicitado: soloDigitos(e.target.value) })}
          />
        </div>
        <div>
          <label style={st.label}>Fecha de último contacto</label>
          <input
            style={{ ...st.input, width: pct(0.5), ...soloLecturaBroker.style }}
            type="date"
            value={form.fecha_ultimo_contacto}
            onChange={(e) => setForm({ ...form, fecha_ultimo_contacto: e.target.value })}
            disabled={soloLecturaBroker.disabled}
          />
        </div>
      </div>

      <label style={st.label}>Observaciones</label>
      <textarea style={{ ...st.input, minHeight: 70, fontFamily: 'Helvetica, Arial, sans-serif' }} value={form.observaciones || ''} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />

      <div style={{ marginTop: 20 }}>
        <button type="submit" style={st.btnPrimary}>Guardar cambios</button>{' '}
        <button type="button" style={st.btnGhost} onClick={onCancel}>Salir</button>
      </div>
    </form>
  );
}

function TareasPanel({ clienteId, puedeEditar }) {
  const [tareas, setTareas] = useState([]);
  const [nueva, setNueva] = useState({ descripcion: '', fecha_programada: '' });
  const [error, setError] = useState('');

  const load = () => fetch(`/api/clientes/${clienteId}/tareas`).then((r) => r.json()).then(setTareas);
  useEffect(() => { load(); }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activas = tareas.filter((t) => !t.cumplida).length;

  const agregar = async (e) => {
    e.preventDefault();
    setError('');
    const r = await fetch(`/api/clientes/${clienteId}/tareas`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nueva),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error); return; }
    setNueva({ descripcion: '', fecha_programada: '' });
    load();
  };

  const marcarCumplida = async (t) => {
    await fetch(`/api/clientes/${clienteId}/tareas/${t.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ descripcion: t.descripcion, fecha_programada: t.fecha_programada, cumplida: !t.cumplida }),
    });
    load();
  };

  const eliminar = async (id) => {
    await fetch(`/api/clientes/${clienteId}/tareas/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div>
      <h4 style={{ color: '#1a3a6e', marginBottom: 8 }}>Tareas / llamadas programadas ({activas}/5 activas)</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <thead><tr><th style={st.th}>Descripción</th><th style={st.th}>Fecha</th><th style={st.th}>Cumplida</th><th style={st.th}></th></tr></thead>
        <tbody>
          {tareas.map((t) => (
            <tr key={t.id}>
              <td style={st.td}>{t.descripcion}</td>
              <td style={st.td}>{String(t.fecha_programada).slice(0, 10)}</td>
              <td style={st.td}>{t.cumplida ? 'Sí' : 'No'}</td>
              <td style={st.td}>
                {puedeEditar && (
                  <>
                    <button style={st.btnGhost} onClick={() => marcarCumplida(t)}>{t.cumplida ? 'Reabrir' : 'Marcar cumplida'}</button>{' '}
                    <button style={st.btnDanger} onClick={() => eliminar(t.id)}>Eliminar</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {puedeEditar && (
        <form onSubmit={agregar} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {error && <div style={{ color: '#dc2626', fontSize: 13, width: '100%' }}>{error}</div>}
          <div>
            <label style={st.label}>Descripción</label>
            <textarea
              style={{ ...st.input, width: 480, minHeight: 108, resize: 'vertical', fontFamily: 'Helvetica, Arial, sans-serif' }}
              rows={4}
              value={nueva.descripcion}
              onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })}
              required
            />
          </div>
          <div>
            <label style={st.label}>Fecha programada</label>
            <input style={st.input} type="date" value={nueva.fecha_programada} onChange={(e) => setNueva({ ...nueva, fecha_programada: e.target.value })} required />
          </div>
          <button type="submit" style={st.btnAccent}>+ Agregar</button>
        </form>
      )}
    </div>
  );
}

function BancosPanel({ clienteId, bancos, puedeEditar, onChanged }) {
  const [registros, setRegistros] = useState([]);
  const [nuevo, setNuevo] = useState({ banco_id: '', fecha_envio_evaluacion: '' });

  const load = () => fetch(`/api/clientes/${clienteId}/bancos`).then((r) => r.json()).then(setRegistros);
  useEffect(() => { load(); }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregar = async (e) => {
    e.preventDefault();
    await fetch(`/api/clientes/${clienteId}/bancos`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nuevo) });
    setNuevo({ banco_id: '', fecha_envio_evaluacion: '' });
    load();
    // Enviar a evaluación cambia el status del cliente en el servidor; refrescamos el detalle para mostrarlo.
    onChanged && onChanged();
  };
  const eliminar = async (id) => { await fetch(`/api/clientes/${clienteId}/bancos/${id}`, { method: 'DELETE' }); load(); };

  return (
    <div>
      <h4 style={{ color: '#1a3a6e', marginBottom: 8 }}>Bancos con expediente enviado</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <thead><tr><th style={st.th}>Banco</th><th style={st.th}>Fecha envío a evaluación</th><th style={st.th}></th></tr></thead>
        <tbody>
          {registros.map((r) => (
            <tr key={r.id}>
              <td style={st.td}>{r.banco_nombre}</td>
              <td style={st.td}>{r.fecha_envio_evaluacion ? String(r.fecha_envio_evaluacion).slice(0, 10) : '—'}</td>
              <td style={st.td}>{puedeEditar && <button style={st.btnDanger} onClick={() => eliminar(r.id)}>Eliminar</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {puedeEditar && (
        <form onSubmit={agregar} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div>
            <label style={st.label}>Banco</label>
            <select style={st.input} value={nuevo.banco_id} onChange={(e) => setNuevo({ ...nuevo, banco_id: e.target.value })} required>
              <option value="">Selecciona…</option>
              {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Fecha envío a evaluación</label>
            <input style={st.input} type="date" value={nuevo.fecha_envio_evaluacion} onChange={(e) => setNuevo({ ...nuevo, fecha_envio_evaluacion: e.target.value })} />
          </div>
          <button type="submit" style={st.btnAccent}>+ Agregar</button>
        </form>
      )}
    </div>
  );
}

function CreditosPanel({ clienteId, bancos, puedeEditar, onChanged }) {
  const [registros, setRegistros] = useState([]);
  const [nuevo, setNuevo] = useState({ banco_id: '', vigencia: '' });

  const load = () => fetch(`/api/clientes/${clienteId}/creditos-autorizados`).then((r) => r.json()).then(setRegistros);
  useEffect(() => { load(); }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregar = async (e) => {
    e.preventDefault();
    await fetch(`/api/clientes/${clienteId}/creditos-autorizados`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nuevo) });
    setNuevo({ banco_id: '', vigencia: '' });
    load();
    // Registrar el crédito autorizado cambia el status del cliente en el servidor; refrescamos el detalle para mostrarlo.
    onChanged && onChanged();
  };
  const eliminar = async (id) => { await fetch(`/api/clientes/${clienteId}/creditos-autorizados/${id}`, { method: 'DELETE' }); load(); };

  return (
    <div>
      <h4 style={{ color: '#1a3a6e', marginBottom: 8 }}>Créditos autorizados</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <thead><tr><th style={st.th}>Banco</th><th style={st.th}>Vigencia</th><th style={st.th}></th></tr></thead>
        <tbody>
          {registros.map((r) => (
            <tr key={r.id}>
              <td style={st.td}>{r.banco_nombre}</td>
              <td style={st.td}>{r.vigencia ? String(r.vigencia).slice(0, 10) : '—'}</td>
              <td style={st.td}>{puedeEditar && <button style={st.btnDanger} onClick={() => eliminar(r.id)}>Eliminar</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {puedeEditar && (
        <form onSubmit={agregar} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div>
            <label style={st.label}>Banco</label>
            <select style={st.input} value={nuevo.banco_id} onChange={(e) => setNuevo({ ...nuevo, banco_id: e.target.value })} required>
              <option value="">Selecciona…</option>
              {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Vigencia</label>
            <input style={st.input} type="date" value={nuevo.vigencia} onChange={(e) => setNuevo({ ...nuevo, vigencia: e.target.value })} />
          </div>
          <button type="submit" style={st.btnAccent}>+ Agregar</button>
        </form>
      )}
    </div>
  );
}

function ClienteDetalle({ cliente, usuarioActual, catalogos, onBack, onChanged }) {
  const esAdmin = usuarioActual.tipo_usuario === 'Administrador';
  const esBroker = usuarioActual.tipo_usuario === 'Broker';
  const puedeEditar = esAdmin || esBroker;
  const [editando, setEditando] = useState(false);
  const [statusSel, setStatusSel] = useState(cliente.status_cliente_id || '');

  // El status puede cambiar en el servidor sin que el usuario lo edite aquí (al enviar a evaluación
  // o al registrar un crédito autorizado), así que el selector debe seguir al cliente recién recargado.
  useEffect(() => { setStatusSel(cliente.status_cliente_id || ''); }, [cliente.status_cliente_id]);

  const guardarCompleto = async (form) => {
    await fetch(`/api/clientes/${cliente.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setEditando(false);
    onChanged();
  };

  const guardarStatus = async () => {
    await fetch(`/api/clientes/${cliente.id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status_cliente_id: statusSel }),
    });
    onChanged();
  };

  if (editando) {
    return (
      <div>
        <button style={st.btnGhost} onClick={() => setEditando(false)}>← Volver al detalle</button>
        <ClienteForm cliente={cliente} catalogos={catalogos} usuarioActual={usuarioActual} onSave={guardarCompleto} onCancel={() => setEditando(false)} />
        <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
          <button style={st.btnGhost} onClick={onBack}>← Volver al menú principal</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button style={st.btnGhost} onClick={onBack}>← Volver al listado</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
        <div><strong>Nombre:</strong> {cliente.nombre_cliente}</div>
        <div><strong>Teléfono:</strong> {cliente.telefono_celular ? formatTelefono(cliente.telefono_celular) : '—'}</div>
        <div><strong>Email:</strong> {cliente.email || '—'}</div>
        <div><strong>Asesor:</strong> {cliente.asesor_nombre || '—'}</div>
        <div><strong>Inmobiliaria:</strong> {cliente.inmobiliaria_nombre || '—'}</div>
        <div><strong>Broker:</strong> {cliente.broker_nombre || '—'}</div>
        <div><strong>Tipo de crédito:</strong> {cliente.tipo_credito_nombre || '—'}</div>
        <div><strong>Tipo de inmueble:</strong> {cliente.tipo_inmueble_nombre || '—'}</div>
        <div><strong>Valor inmueble:</strong> {cliente.valor_inmueble != null && cliente.valor_inmueble !== '' ? formatMoney(cliente.valor_inmueble) : '—'}</div>
        <div><strong>Valor crédito solicitado:</strong> {cliente.valor_credito_solicitado != null && cliente.valor_credito_solicitado !== '' ? formatMoney(cliente.valor_credito_solicitado) : '—'}</div>
        <div><strong>Último contacto:</strong> {cliente.fecha_ultimo_contacto ? String(cliente.fecha_ultimo_contacto).slice(0, 10) : '—'}</div>
        <div><strong>Observaciones:</strong> {cliente.observaciones || '—'}</div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div>
          <label style={st.label}>Status</label>
          <select style={st.input} value={statusSel} onChange={(e) => setStatusSel(e.target.value)} disabled={!puedeEditar}>
            <option value="">Selecciona…</option>
            {catalogos.status.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
        {puedeEditar && <button style={st.btnAccent} onClick={guardarStatus}>Guardar cambios de status</button>}
        {esAdmin && <button style={st.btnPrimary} onClick={() => setEditando(true)}>Editar cliente completo</button>}
        {esBroker && <button style={st.btnPrimary} onClick={() => setEditando(true)}>Editar cliente</button>}
        <button style={st.btnGhost} onClick={onBack}>Salir</button>
      </div>

      <div style={{ marginTop: 28, borderTop: '1px solid #eee', paddingTop: 16 }}>
        <TareasPanel clienteId={cliente.id} puedeEditar={puedeEditar} />
      </div>
      <div style={{ marginTop: 28, borderTop: '1px solid #eee', paddingTop: 16 }}>
        <BancosPanel clienteId={cliente.id} bancos={catalogos.bancos} puedeEditar={puedeEditar} onChanged={onChanged} />
      </div>
      <div style={{ marginTop: 28, borderTop: '1px solid #eee', paddingTop: 16 }}>
        <CreditosPanel clienteId={cliente.id} bancos={catalogos.bancos} puedeEditar={puedeEditar} onChanged={onChanged} />
      </div>

      <div style={{ marginTop: 28, borderTop: '1px solid #eee', paddingTop: 16, display: 'flex', gap: 8 }}>
        {puedeEditar && <button style={st.btnAccent} onClick={guardarStatus}>Guardar cambios</button>}
        <button style={st.btnGhost} onClick={onBack}>← Volver al menú principal</button>
      </div>
    </div>
  );
}

export default function Clientes({ usuarioActual, clienteIdInicial, onClienteIdConsumido }) {
  const [clientes, setClientes] = useState([]);
  const [catalogos, setCatalogos] = useState({ asesores: [], brokers: [], tipoCredito: [], tipoInmueble: [], status: [], bancos: [] });
  const [seleccionado, setSeleccionado] = useState(null);
  const [creando, setCreando] = useState(false);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroAsesorId, setFiltroAsesorId] = useState('');
  const [filtroStatusIds, setFiltroStatusIds] = useState([]);
  const esAdmin = usuarioActual.tipo_usuario === 'Administrador';
  const esBroker = usuarioActual.tipo_usuario === 'Broker';

  const loadClientes = () => fetch('/api/clientes').then((r) => r.json()).then(setClientes);

  useEffect(() => {
    loadClientes();
    Promise.all([
      fetch('/api/catalogos/asesores-inmobiliarios').then((r) => r.json()),
      fetch('/api/catalogos/brokers').then((r) => r.json()),
      fetch('/api/catalogos/tipo-credito').then((r) => r.json()),
      fetch('/api/catalogos/tipo-inmueble').then((r) => r.json()),
      fetch('/api/catalogos/status-cliente').then((r) => r.json()),
      fetch('/api/catalogos/bancos').then((r) => r.json()),
    ]).then(([asesores, brokers, tipoCredito, tipoInmueble, status, bancos]) => {
      setCatalogos({ asesores, brokers, tipoCredito, tipoInmueble, status, bancos });
    });
  }, []);

  const abrirDetalle = async (id) => {
    const r = await fetch(`/api/clientes/${id}`);
    const d = await r.json();
    setSeleccionado(d);
  };

  // Al llegar desde una notificación de tarea, abre directo el expediente de ese cliente.
  useEffect(() => {
    if (clienteIdInicial) {
      setCreando(false);
      abrirDetalle(clienteIdInicial);
      onClienteIdConsumido && onClienteIdConsumido();
    }
  }, [clienteIdInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  const crear = async (form) => {
    await fetch('/api/clientes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setCreando(false);
    loadClientes();
  };

  if (creando) {
    return (
      <div style={st.card}>
        <h2 style={st.sectionTitle}>Nuevo cliente</h2>
        <ClienteForm cliente={null} catalogos={catalogos} usuarioActual={usuarioActual} onSave={crear} onCancel={() => setCreando(false)} />
      </div>
    );
  }

  if (seleccionado) {
    return (
      <div style={st.card}>
        <h2 style={st.sectionTitle}>Detalle del cliente</h2>
        <ClienteDetalle
          cliente={seleccionado}
          usuarioActual={usuarioActual}
          catalogos={catalogos}
          onBack={() => setSeleccionado(null)}
          onChanged={() => { abrirDetalle(seleccionado.id); loadClientes(); }}
        />
      </div>
    );
  }

  const asesoresOrdenados = ordenarAlfabetico(catalogos.asesores);

  const toggleStatusFiltro = (id) => {
    setFiltroStatusIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const limpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroAsesorId('');
    setFiltroStatusIds([]);
  };

  const hayFiltrosActivos = filtroNombre.trim() !== '' || filtroAsesorId !== '' || filtroStatusIds.length > 0;

  const clientesFiltrados = clientes.filter((c) => {
    const coincideNombre = !filtroNombre.trim() || String(c.nombre_cliente || '').toLowerCase().includes(filtroNombre.trim().toLowerCase());
    const coincideAsesor = !filtroAsesorId || String(c.asesor_inmobiliario_id) === String(filtroAsesorId);
    const coincideStatus = filtroStatusIds.length === 0 || filtroStatusIds.includes(String(c.status_cliente_id));
    return coincideNombre && coincideAsesor && coincideStatus;
  });

  return (
    <div style={st.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={st.sectionTitle}>Clientes</h2>
        {(esAdmin || esBroker) && <button style={st.btnAccent} onClick={() => setCreando(true)}>+ Nuevo cliente</button>}
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 16 }}>
        <div>
          <label style={st.label}>Buscar por nombre</label>
          <input
            style={{ ...st.input, width: 220 }}
            value={filtroNombre}
            onChange={(e) => setFiltroNombre(e.target.value)}
            placeholder="Nombre del cliente…"
          />
        </div>
        <div>
          <label style={st.label}>Asesor inmobiliario</label>
          <select style={{ ...st.input, width: 200 }} value={filtroAsesorId} onChange={(e) => setFiltroAsesorId(e.target.value)}>
            <option value="">Todos</option>
            {asesoresOrdenados.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Status (uno o varios)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', minWidth: 220, boxSizing: 'border-box' }}>
            {catalogos.status.map((s) => (
              <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filtroStatusIds.includes(String(s.id))}
                  onChange={() => toggleStatusFiltro(String(s.id))}
                />
                {s.nombre}
              </label>
            ))}
          </div>
        </div>
        {hayFiltrosActivos && (
          <button type="button" style={{ ...st.btnGhost, alignSelf: 'flex-end' }} onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr>
            <th style={st.th}>Nombre</th>
            <th style={st.th}>Asesor / Inmobiliaria</th>
            <th style={st.th}>Broker</th>
            <th style={st.th}>Status</th>
            <th style={st.th}>Último contacto</th>
            <th style={st.th}></th>
          </tr>
        </thead>
        <tbody>
          {clientesFiltrados.map((c) => (
            <tr key={c.id}>
              <td style={st.td}>{c.nombre_cliente}</td>
              <td style={st.td}>{c.asesor_nombre} {c.inmobiliaria_nombre ? `(${c.inmobiliaria_nombre})` : ''}</td>
              <td style={st.td}>{c.broker_nombre}</td>
              <td style={st.td}>{c.status_cliente_nombre || '—'}</td>
              <td style={st.td}>{c.fecha_ultimo_contacto ? String(c.fecha_ultimo_contacto).slice(0, 10) : '—'}</td>
              <td style={st.td}><button style={st.btnGhost} onClick={() => abrirDetalle(c.id)}>Ver</button></td>
            </tr>
          ))}
          {clientes.length === 0 && (
            <tr><td style={st.td} colSpan={6}>No hay clientes registrados.</td></tr>
          )}
          {clientes.length > 0 && clientesFiltrados.length === 0 && (
            <tr><td style={st.td} colSpan={6}>Ningún cliente coincide con los filtros aplicados.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
